/*
  ReportsPanel.jsx — Reports tab for the Collection Planning module.
  Contains: ReportsPanel (top-level, with 5 tabs), OutstandingReport, and
  ProjectOutstandingAccordion (drill-down list). Extracted from
  CollectionPlanningPage.jsx (Feb 2026 refactor).
*/
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, RefreshCw, AlertTriangle, ChevronDown, ChevronRight, Camera,
} from "lucide-react";
import { fmt, PRESETS, REVENUE_STREAMS, getDatePreset } from "./planningUtils";

function ProjectOutstandingAccordion({ projects, onSnapshot }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (pid) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    return next;
  });
  const expandAll = () => setExpanded(new Set(projects.map(p => p.id)));
  const collapseAll = () => setExpanded(new Set());
  const freqLabel = (f) => ({ one_time: "One-time", monthly: "Monthly", quarterly: "Quarterly", annually: "Annually" }[f] || f);
  return (
    <div className="space-y-3" data-testid="project-outstanding-accordion">
      <div className="flex items-center justify-between text-xs">
        <p className="text-slate-500">Tap a project row to see per-billing-line breakdown</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={expandAll} data-testid="outstanding-expand-all">Expand all</Button>
          <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={collapseAll} data-testid="outstanding-collapse-all">Collapse all</Button>
        </div>
      </div>
      <Card>
        <CardContent className="p-0 divide-y divide-slate-100">
          {projects.length === 0 && <p className="text-center text-slate-400 py-12 text-sm">No projects.</p>}
          {projects.map(p => {
            const isOpen = expanded.has(p.id);
            return (
              <div key={p.id} data-testid={`outstanding-project-${p.id}`}>
                <div className="w-full px-4 py-3 hover:bg-slate-50 transition-colors grid grid-cols-12 gap-3 items-center">
                  <button type="button" onClick={() => toggle(p.id)} className="col-span-12 sm:col-span-4 flex items-center gap-2 text-left min-w-0" data-testid={`outstanding-toggle-${p.id}`}>
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                        {p.billing_lines_count} line{p.billing_lines_count !== 1 ? "s" : ""}
                        {typeof p.age_months === "number" && (
                          <span className="ml-1 text-slate-500">· Age: <strong className="text-slate-700">{p.age_months} {p.age_months === 1 ? "month" : "months"}</strong></span>
                        )}
                        {p.status !== "active" && <span className="ml-1 text-amber-600">· {p.status}</span>}
                      </p>
                    </div>
                  </button>
                  <div className="col-span-4 sm:col-span-2 text-right">
                    <p className="text-[10px] text-slate-400 uppercase">Expected</p>
                    <p className="text-sm font-semibold text-[#0066CC]">{fmt(p.expected_billed || p.total_value)}</p>
                  </div>
                  <div className="col-span-4 sm:col-span-2 text-right">
                    <p className="text-[10px] text-slate-400 uppercase">Collected</p>
                    <p className="text-sm font-semibold text-emerald-600">{fmt(p.total_collected)}</p>
                  </div>
                  <div className="col-span-4 sm:col-span-2 text-right">
                    <p className="text-[10px] text-slate-400 uppercase">Outstanding</p>
                    <p className={`text-sm font-semibold ${p.outstanding > 0 ? "text-red-600" : "text-slate-400"}`}>{fmt(p.outstanding)}</p>
                  </div>
                  <div className="col-span-12 sm:col-span-2 flex items-center justify-end gap-2">
                    <div className="inline-flex items-center gap-1.5 flex-1 sm:flex-initial">
                      <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, p.collection_pct)}%` }} />
                      </div>
                      <span className="text-[10px] font-semibold">{p.collection_pct}%</span>
                    </div>
                    <button onClick={() => onSnapshot(p.id)} className="text-[#0066CC] hover:text-[#0052A3]" title="Snapshot"><Camera className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {isOpen && (
                  <div className="bg-slate-50/60 px-4 py-3" data-testid={`outstanding-lines-${p.id}`}>
                    {(!p.billing_lines || p.billing_lines.length === 0) ? (
                      <p className="text-xs text-slate-400 italic px-4 py-2">No billing lines configured on this project.</p>
                    ) : (
                      <div className="space-y-1">
                        <div className="grid grid-cols-12 gap-3 text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-3 pb-1.5">
                          <span className="col-span-3">Line</span>
                          <span className="col-span-2">Frequency</span>
                          <span className="col-span-2 text-right">Amount</span>
                          <span className="col-span-1 text-right">Periods</span>
                          <span className="col-span-2 text-right">Expected</span>
                          <span className="col-span-1 text-right">Collected</span>
                          <span className="col-span-1 text-right">Outstanding</span>
                        </div>
                        {p.billing_lines.map(l => (
                          <div key={l.id} className="grid grid-cols-12 gap-3 items-center px-3 py-2 rounded-lg text-xs bg-white border border-slate-100" data-testid={`outstanding-line-${l.id}`}>
                            <div className="col-span-3">
                              <span className="text-[11px] font-semibold bg-blue-50 text-[#0066CC] px-1.5 py-0.5 rounded">{l.type}</span>
                              {l.start_date && <span className="text-[10px] text-slate-400 ml-2">since {l.start_date}</span>}
                              {l.end_date && <span className="text-[10px] text-slate-500 ml-2">→ {l.end_date}</span>}
                              {l.end_date && new Date(l.end_date) < new Date() && (
                                <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">ENDED</span>
                              )}
                            </div>
                            <div className="col-span-2 text-slate-600">{freqLabel(l.frequency)}</div>
                            <div className="col-span-2 text-right text-slate-800 font-mono">{fmt(l.amount)}</div>
                            <div className="col-span-1 text-right text-slate-500">{l.completed_periods}</div>
                            <div className="col-span-2 text-right font-semibold text-[#0066CC]">{fmt(l.expected)}</div>
                            <div className="col-span-1 text-right font-semibold text-emerald-600">{fmt(l.collected)}</div>
                            <div className={`col-span-1 text-right font-semibold ${l.outstanding > 0 ? "text-red-600" : "text-slate-400"}`}>{fmt(l.outstanding)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

/* --------------------------------------------------------------
   Pivot view — Project Type × Billing Type outstanding matrix.
   Rows = 5 project types, Columns = billing types (from BILLING_TYPES),
   Cells = outstanding ₹ + project count. Click a cell to expand an
   inline mini-list, or "Show in detailed view" to jump to the accordion
   with filter pre-applied.
   -------------------------------------------------------------- */
function OutstandingPivot({ data, onJumpToDetailed }) {
  const [expandedCell, setExpandedCell] = useState(null); // "ptype||btype"

  const { matrix, ptypeTotals, btypeTotals, grandTotal, allBtypes } = useMemo(() => {
    const types = REVENUE_STREAMS.map(s => s.key);
    const btSet = new Set();
    const m = {}; // { ptype: { btype: { outstanding, projectCount, projectList: [] } } }
    for (const t of types) m[t] = {};
    const pt = Object.fromEntries(types.map(t => [t, { outstanding: 0, projectCount: 0 }]));
    const bt = {};
    let gt = 0;

    for (const p of (data.projects || [])) {
      const type = types.includes(p.project_type) ? p.project_type : "cloud_others";
      const projLines = p.billing_lines || [];
      const contribution = {}; // per-billing-type outstanding for this project
      for (const ln of projLines) {
        if ((ln.outstanding || 0) <= 0) continue;
        const btype = ln.type || "Others";
        btSet.add(btype);
        contribution[btype] = (contribution[btype] || 0) + ln.outstanding;
      }
      for (const [btype, out] of Object.entries(contribution)) {
        if (!m[type][btype]) m[type][btype] = { outstanding: 0, projectCount: 0, projects: [] };
        m[type][btype].outstanding += out;
        m[type][btype].projectCount += 1;
        m[type][btype].projects.push({ id: p.id, name: p.name, outstanding: out, age_months: p.age_months });
        pt[type].outstanding += out;
        pt[type].projectCount += 1;
        bt[btype] = (bt[btype] || 0) + out;
        gt += out;
      }
    }
    // Sort billing types by prevalence for readable display
    const btArr = [...btSet].sort((a, b) => (bt[b] || 0) - (bt[a] || 0));
    return { matrix: m, ptypeTotals: pt, btypeTotals: bt, grandTotal: gt, allBtypes: btArr };
  }, [data]);

  const cellColor = (v, max) => {
    if (v <= 0) return "";
    const intensity = Math.min(1, v / (max || 1));
    // red gradient for higher outstanding
    if (intensity > 0.66) return "bg-red-100 text-red-800";
    if (intensity > 0.33) return "bg-amber-50 text-amber-800";
    return "bg-blue-50 text-slate-700";
  };
  const maxCell = Math.max(1, ...Object.values(matrix).flatMap(cols => Object.values(cols).map(c => c.outstanding)));

  if (allBtypes.length === 0) {
    return <Card><CardContent className="p-12 text-center text-slate-400"><p className="text-sm">No outstanding amounts to display in the matrix.</p></CardContent></Card>;
  }

  return (
    <div className="space-y-3" data-testid="outstanding-pivot">
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b sticky top-0">
              <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Project Type ↓ / Billing Point →</th>
              {allBtypes.map(bt => (
                <th key={bt} className="text-right py-2.5 px-3 font-semibold text-slate-600 whitespace-nowrap">{bt}</th>
              ))}
              <th className="text-right py-2.5 px-3 font-bold text-slate-700 border-l bg-slate-100">Total</th>
            </tr>
          </thead>
          <tbody>
            {REVENUE_STREAMS.map(stream => {
              const rowT = ptypeTotals[stream.key];
              if (!rowT.outstanding && !rowT.projectCount) {
                return (
                  <tr key={stream.key} className="border-b border-slate-50">
                    <td className="py-2.5 px-3 font-medium" style={{ color: stream.color }}>{stream.label}</td>
                    {allBtypes.map(bt => <td key={bt} className="py-2.5 px-3 text-right text-slate-300">—</td>)}
                    <td className="py-2.5 px-3 text-right text-slate-400 border-l bg-slate-50">—</td>
                  </tr>
                );
              }
              return (
                <React.Fragment key={stream.key}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-2.5 px-3 font-medium" style={{ color: stream.color }} data-testid={`pivot-row-${stream.key}`}>
                      {stream.label}
                      <span className="ml-1.5 text-[10px] text-slate-400">({rowT.projectCount})</span>
                    </td>
                    {allBtypes.map(bt => {
                      const cell = matrix[stream.key][bt];
                      const cellKey = `${stream.key}||${bt}`;
                      if (!cell || cell.outstanding <= 0) return <td key={bt} className="py-2.5 px-3 text-right text-slate-300">—</td>;
                      const isOpen = expandedCell === cellKey;
                      return (
                        <td key={bt} className={`py-2 px-2 text-right ${cellColor(cell.outstanding, maxCell)}`}>
                          <button
                            className="w-full text-right hover:underline"
                            onClick={() => setExpandedCell(isOpen ? null : cellKey)}
                            data-testid={`pivot-cell-${stream.key}-${bt.replace(/\s+/g, '_')}`}
                            title="Click to expand · Ctrl-click to jump to detailed view"
                            onDoubleClick={(e) => { e.preventDefault(); onJumpToDetailed?.({ ptype: stream.key, btype: bt }); }}
                          >
                            <div className="font-bold">{fmt(cell.outstanding)}</div>
                            <div className="text-[9px] opacity-70">{cell.projectCount} {cell.projectCount === 1 ? "proj" : "projs"}</div>
                          </button>
                        </td>
                      );
                    })}
                    <td className="py-2.5 px-3 text-right font-bold border-l bg-slate-50">
                      {fmt(rowT.outstanding)}
                      <div className="text-[9px] text-slate-500 font-normal">{rowT.projectCount} projs</div>
                    </td>
                  </tr>
                  {expandedCell && expandedCell.startsWith(`${stream.key}||`) && (
                    (() => {
                      const bt = expandedCell.split("||")[1];
                      const cell = matrix[stream.key][bt];
                      if (!cell) return null;
                      return (
                        <tr className="bg-slate-50/70">
                          <td colSpan={allBtypes.length + 2} className="p-3 border-b" data-testid={`pivot-expand-${stream.key}-${bt.replace(/\s+/g, '_')}`}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[11px] font-semibold text-slate-700">
                                <span style={{ color: stream.color }}>{stream.label}</span>
                                <span className="mx-1.5 text-slate-400">×</span>
                                <span className="text-[#0066CC]">{bt}</span>
                                <span className="ml-2 text-slate-500">— {cell.projectCount} project{cell.projectCount === 1 ? "" : "s"} · {fmt(cell.outstanding)} pending</span>
                              </p>
                              <button
                                onClick={() => onJumpToDetailed?.({ ptype: stream.key, btype: bt })}
                                className="text-[11px] text-[#0066CC] hover:underline font-medium"
                                data-testid={`pivot-jump-${stream.key}-${bt.replace(/\s+/g, '_')}`}
                              >
                                Show in detailed view →
                              </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                              {[...cell.projects].sort((a, b) => b.outstanding - a.outstanding).map(p => (
                                <div key={p.id} className="flex items-center justify-between gap-2 bg-white rounded px-2.5 py-1.5 border border-slate-200 text-[11px]">
                                  <span className="text-slate-700 truncate min-w-0" title={p.name}>{p.name}</span>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    {typeof p.age_months === "number" && p.age_months > 0 && (
                                      <span className="text-[9px] text-slate-400">{p.age_months}mo</span>
                                    )}
                                    <span className="font-semibold text-red-600">{fmt(p.outstanding)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })()
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 border-t-2 border-slate-300 font-bold">
              <td className="py-2.5 px-3 text-slate-700">Total</td>
              {allBtypes.map(bt => (
                <td key={bt} className="py-2.5 px-3 text-right text-slate-800">
                  {btypeTotals[bt] ? fmt(btypeTotals[bt]) : "—"}
                </td>
              ))}
              <td className="py-2.5 px-3 text-right text-red-700 border-l bg-slate-200">{fmt(grandTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </CardContent></Card>
      <p className="text-[10px] text-slate-500 italic">
        Click a cell to expand the list of contributing projects · Click <strong>Show in detailed view</strong> or double-click a cell to jump into the accordion with the filter pre-applied.
      </p>
    </div>
  );
}

/* --------------------------------------------------------------
   Report View — flat ERP-style outstanding report: one row per
   (project × billing line), filterable by as-on date (month / quarter
   end), sortable, and exportable to CSV/Excel.
   -------------------------------------------------------------- */
function OutstandingReportView() {
  const [asOn, setAsOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [preset, setPreset] = useState("today");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ptypeFilter, setPtypeFilter] = useState("all");
  const [btypeFilter, setBtypeFilter] = useState("all");
  const [showZero, setShowZero] = useState(false);
  const [sortBy, setSortBy] = useState({ key: "outstanding", dir: "desc" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/collections/reports/project-outstanding?end_date=${asOn}&start_date=${asOn}`);
      const flat = [];
      for (const p of (r.data.projects || [])) {
        for (const ln of (p.billing_lines || [])) {
          flat.push({
            project_id: p.id,
            project_name: p.name,
            client_name: p.client_name || "",
            project_type: p.project_type || "cloud_others",
            project_status: p.status || "active",
            billing_type: ln.type || "Others",
            frequency: ln.frequency || "one_time",
            start_date: ln.start_date || "",
            end_date: ln.end_date || "",
            periods: ln.completed_periods || 0,
            expected: ln.expected || 0,
            collected: ln.collected || 0,
            outstanding: ln.outstanding || 0,
            advance: 0,
            age_months: p.age_months ?? "",
            sales_owner: p.sales_member_name || "",
            _synthetic: false,
          });
        }
        // Synthesise an "Unallocated Receipts" pseudo-row per project so numbers reconcile perfectly.
        // Positive value means receipts exist on this project but aren't linked to a billing line.
        if ((p.unallocated_collected || 0) > 0) {
          flat.push({
            project_id: p.id,
            project_name: p.name,
            client_name: p.client_name || "",
            project_type: p.project_type || "cloud_others",
            project_status: p.status || "active",
            billing_type: "Unallocated",
            frequency: "—",
            start_date: "",
            end_date: "",
            periods: 0,
            expected: 0,
            collected: p.unallocated_collected || 0,
            outstanding: 0,
            advance: 0,
            age_months: p.age_months ?? "",
            sales_owner: p.sales_member_name || "",
            _synthetic: true,
          });
        }
        // Advance pseudo-row when the project has collected more than accrued (client paid ahead).
        if ((p.advance || 0) > 0) {
          flat.push({
            project_id: p.id,
            project_name: p.name,
            client_name: p.client_name || "",
            project_type: p.project_type || "cloud_others",
            project_status: p.status || "active",
            billing_type: "Advance",
            frequency: "—",
            start_date: "",
            end_date: "",
            periods: 0,
            expected: 0,
            collected: 0,
            outstanding: 0,
            advance: p.advance || 0,
            age_months: p.age_months ?? "",
            sales_owner: p.sales_member_name || "",
            _synthetic: true,
          });
        }
      }
      setRows(flat);
      setSummary(r.data.summary || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    }
    setLoading(false);
  }, [asOn]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (id) => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(); // 0-idx
    let d;
    if (id === "today") d = now;
    else if (id === "month_end") d = new Date(y, m + 1, 0);
    else if (id === "prev_month_end") d = new Date(y, m, 0);
    else if (id === "quarter_end") {
      const q = Math.floor(m / 3);
      d = new Date(y, (q + 1) * 3, 0);
    } else if (id === "prev_quarter_end") {
      const q = Math.floor(m / 3);
      d = new Date(y, q * 3, 0);
    } else if (id === "fy_end") {
      d = m >= 3 ? new Date(y + 1, 2, 31) : new Date(y, 2, 31);
    }
    setPreset(id);
    setAsOn(d.toISOString().slice(0, 10));
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (!showZero) out = out.filter(r => r.outstanding > 0);
    if (ptypeFilter !== "all") out = out.filter(r => r.project_type === ptypeFilter);
    if (btypeFilter !== "all") out = out.filter(r => r.billing_type === btypeFilter);
    const dir = sortBy.dir === "asc" ? 1 : -1;
    out = [...out].sort((a, b) => {
      const va = a[sortBy.key], vb = b[sortBy.key];
      if (typeof va === "number") return (va - vb) * dir;
      return String(va || "").localeCompare(String(vb || "")) * dir;
    });
    return out;
  }, [rows, showZero, ptypeFilter, btypeFilter, sortBy]);

  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    expected: acc.expected + r.expected,
    collected: acc.collected + r.collected,
    outstanding: acc.outstanding + r.outstanding,
    advance: acc.advance + (r.advance || 0),
  }), { expected: 0, collected: 0, outstanding: 0, advance: 0 }), [filtered]);

  const distinctBtypes = useMemo(() => [...new Set(rows.map(r => r.billing_type))].sort(), [rows]);

  const exportCSV = () => {
    const cols = ["Project", "Project Type", "Billing Line", "Frequency", "Start Date", "End Date", "Completed Periods", "Expected", "Collected", "Outstanding", "Advance", "Age (mo)", "Sales Owner"];
    const stream = (v) => v == null ? "" : String(v).replace(/"/g, '""');
    const esc = (v) => `"${stream(v)}"`;
    const lines = [cols.map(esc).join(",")];
    for (const r of filtered) {
      const ptype = REVENUE_STREAMS.find(s => s.key === r.project_type)?.label || r.project_type;
      lines.push([r.project_name, ptype, r.billing_type, r.frequency.replace("_", " "), r.start_date, r.end_date, r.periods, r.expected, r.collected, r.outstanding, r.advance || 0, r.age_months, r.sales_owner].map(esc).join(","));
    }
    lines.push(["TOTAL", "", "", "", "", "", "", totals.expected, totals.collected, totals.outstanding, totals.advance, "", ""].map(esc).join(","));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outstanding-report-${asOn}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} rows`);
  };

  const setSort = (k) => setSortBy(s => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: k === "outstanding" || k === "expected" || k === "collected" || k === "periods" ? "desc" : "asc" });
  const sortIcon = (k) => sortBy.key !== k ? "" : sortBy.dir === "asc" ? " ↑" : " ↓";

  const PRESET_BTNS = [
    { id: "today",             label: "Today" },
    { id: "month_end",         label: "Current Month-End" },
    { id: "prev_month_end",    label: "Prev Month-End" },
    { id: "quarter_end",       label: "Current Quarter-End" },
    { id: "prev_quarter_end",  label: "Prev Quarter-End" },
    { id: "fy_end",            label: "FY-End (Mar 31)" },
  ];

  return (
    <div className="space-y-3" data-testid="outstanding-report-view">
      {/* Toolbar */}
      <Card><CardContent className="p-3 space-y-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-700">As On:</span>
          <Input type="date" className="h-8 w-[140px] text-xs" value={asOn} onChange={e => { setAsOn(e.target.value); setPreset(""); }} data-testid="report-as-on" />
          {PRESET_BTNS.map(b => (
            <button key={b.id} data-testid={`report-preset-${b.id}`} onClick={() => applyPreset(b.id)}
              className={`px-2.5 py-1 text-[11px] rounded-full border transition ${preset === b.id ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {b.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={exportCSV} disabled={loading || filtered.length === 0} data-testid="report-export-csv">
              Export CSV
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-600">
          <span>Filter:</span>
          <select value={ptypeFilter} onChange={e => setPtypeFilter(e.target.value)} className="h-7 border rounded px-2 bg-white" data-testid="report-filter-ptype">
            <option value="all">All Project Types</option>
            {REVENUE_STREAMS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={btypeFilter} onChange={e => setBtypeFilter(e.target.value)} className="h-7 border rounded px-2 bg-white" data-testid="report-filter-btype">
            <option value="all">All Billing Lines</option>
            {distinctBtypes.map(bt => <option key={bt} value={bt}>{bt}</option>)}
          </select>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showZero} onChange={e => setShowZero(e.target.checked)} data-testid="report-show-zero" />
            <span>Include lines with ₹0 outstanding</span>
          </label>
          <span className="ml-auto text-slate-500">
            {filtered.length} row{filtered.length === 1 ? "" : "s"} · {loading && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}
          </span>
        </div>
      </CardContent></Card>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white border rounded p-2 text-center"><p className="text-[10px] text-slate-500 uppercase">Expected</p><p className="text-base font-bold text-[#0066CC]">{fmt(totals.expected)}</p></div>
        <div className="bg-white border rounded p-2 text-center"><p className="text-[10px] text-slate-500 uppercase">Collected</p><p className="text-base font-bold text-emerald-600">{fmt(totals.collected)}</p></div>
        <div className="bg-white border rounded p-2 text-center"><p className="text-[10px] text-slate-500 uppercase">Outstanding</p><p className="text-base font-bold text-red-600">{fmt(totals.outstanding)}</p></div>
      </div>

      {/* Excel-style table */}
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="report-table">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr className="border-b border-slate-200">
              {[
                ["project_name","Project"],
                ["project_type","Type"],
                ["billing_type","Billing Line"],
                ["frequency","Freq."],
                ["start_date","Start"],
                ["end_date","End"],
                ["periods","Periods"],
                ["expected","Expected"],
                ["collected","Collected"],
                ["outstanding","Outstanding"],
                ["advance","Advance"],
                ["age_months","Age (mo)"],
                ["sales_owner","Owner"],
              ].map(([k, l]) => (
                <th key={k} onClick={() => setSort(k)}
                    className={`text-${["expected","collected","outstanding","advance","periods","age_months"].includes(k) ? "right" : "left"} px-2 py-2 font-semibold text-slate-600 cursor-pointer select-none whitespace-nowrap hover:bg-slate-100`}
                    data-testid={`report-col-${k}`}>
                  {l}{sortIcon(k)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={13} className="text-center text-slate-400 py-10">No outstanding rows for this filter.</td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={`${r.project_id}-${r.billing_type}-${i}`} className={`border-b border-slate-50 hover:bg-blue-50/40 ${r._synthetic ? "bg-amber-50/30" : ""}`}>
                <td className="px-2 py-1.5 font-medium text-slate-800 whitespace-nowrap max-w-[200px] truncate" title={r.project_name}>{r.project_name}</td>
                <td className="px-2 py-1.5">
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${REVENUE_STREAMS.find(s => s.key === r.project_type)?.color || "#64748B"}20`, color: REVENUE_STREAMS.find(s => s.key === r.project_type)?.color || "#64748B" }}>
                    {REVENUE_STREAMS.find(s => s.key === r.project_type)?.label || r.project_type}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">
                  {r._synthetic ? (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.billing_type === "Advance" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.billing_type}</span>
                  ) : r.billing_type}
                </td>
                <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{r.frequency.replace("_", " ")}</td>
                <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{r.start_date || "—"}</td>
                <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{r.end_date || "—"}</td>
                <td className="px-2 py-1.5 text-right text-slate-600">{r.periods || "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono text-[#0066CC]">{r.expected ? fmt(r.expected) : "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono text-emerald-600">{r.collected ? fmt(r.collected) : "—"}</td>
                <td className={`px-2 py-1.5 text-right font-mono font-bold ${r.outstanding > 0 ? "text-red-600" : "text-slate-300"}`}>{r.outstanding ? fmt(r.outstanding) : "—"}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${r.advance > 0 ? "text-emerald-700 font-bold" : "text-slate-300"}`}>{r.advance > 0 ? fmt(r.advance) : "—"}</td>
                <td className="px-2 py-1.5 text-right text-slate-500">{r.age_months ?? "—"}</td>
                <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap max-w-[140px] truncate" title={r.sales_owner}>{r.sales_owner || "—"}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="bg-slate-100 border-t-2 border-slate-300 font-bold">
                <td colSpan={7} className="px-2 py-2 text-right text-slate-700">TOTAL</td>
                <td className="px-2 py-2 text-right font-mono text-[#0066CC]">{fmt(totals.expected)}</td>
                <td className="px-2 py-2 text-right font-mono text-emerald-700">{fmt(totals.collected)}</td>
                <td className="px-2 py-2 text-right font-mono text-red-700">{fmt(totals.outstanding)}</td>
                <td className="px-2 py-2 text-right font-mono text-emerald-700">{totals.advance > 0 ? fmt(totals.advance) : "—"}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </CardContent></Card>
      {summary?.as_on && (
        <p className="text-[10px] text-slate-500 italic">
          Accrual snapshot as on <strong>{summary.as_on}</strong>. Click any column header to sort. Export CSV opens cleanly in Excel/Google Sheets.
        </p>
      )}
    </div>
  );
}


function OutstandingReport({ data, onSnapshot }) {
  const [subview, setSubview] = useState("pivot"); // "pivot" | "detailed" | "report"
  const [pivotFilter, setPivotFilter] = useState(null); // {ptype, btype}
  const [freqFilter, setFreqFilter] = useState("all");
  const FREQ_TABS = [
    { id: "all", label: "All" },
    { id: "one_time", label: "One Time" },
    { id: "monthly", label: "Monthly" },
    { id: "quarterly", label: "Quarterly" },
    { id: "annually", label: "Annually" },
  ];

  const { projects, summary } = (() => {
    let baseProjects = data.projects;
    let sumTag = data.summary || {};
    // Apply pivot filter (project_type + billing_type) if set — takes precedence
    if (pivotFilter) {
      const { ptype, btype } = pivotFilter;
      const filteredProjects = [];
      let sE = 0, sC = 0, sO = 0;
      for (const p of baseProjects) {
        if ((p.project_type || "cloud_others") !== ptype) continue;
        const lines = (p.billing_lines || []).filter(l => (l.type || "Others") === btype);
        if (lines.length === 0) continue;
        const exp = lines.reduce((s, l) => s + (l.expected || 0), 0);
        const col = lines.reduce((s, l) => s + (l.collected || 0), 0);
        const out = Math.max(0, exp - col);
        const pct = exp > 0 ? Math.round((col / exp) * 1000) / 10 : 0;
        filteredProjects.push({ ...p, billing_lines: lines, billing_lines_count: lines.length, expected_billed: exp, total_value: exp, total_collected: col, outstanding: out, collection_pct: pct });
        sE += exp; sC += col; sO += out;
      }
      filteredProjects.sort((a, b) => b.outstanding - a.outstanding);
      return { projects: filteredProjects, summary: { total_projects: filteredProjects.length, expected_billed: sE, total_collected: sC, total_outstanding: sO, as_on: sumTag.as_on } };
    }
    if (freqFilter === "all") return { projects: data.projects, summary: sumTag };
    const filteredProjects = [];
    let sumExp = 0, sumCol = 0, sumOut = 0;
    for (const p of data.projects) {
      const lines = (p.billing_lines || []).filter(l => (l.frequency || "one_time") === freqFilter);
      if (lines.length === 0) continue;
      const exp = lines.reduce((s, l) => s + (l.expected || 0), 0);
      const col = lines.reduce((s, l) => s + (l.collected || 0), 0);
      const out = Math.max(0, exp - col);
      const pct = exp > 0 ? Math.round((col / exp) * 1000) / 10 : 0;
      filteredProjects.push({ ...p, billing_lines: lines, billing_lines_count: lines.length, expected_billed: exp, total_value: exp, total_collected: col, outstanding: out, collection_pct: pct });
      sumExp += exp; sumCol += col; sumOut += out;
    }
    filteredProjects.sort((a, b) => b.outstanding - a.outstanding);
    return { projects: filteredProjects, summary: { total_projects: filteredProjects.length, expected_billed: sumExp, total_collected: sumCol, total_outstanding: sumOut, as_on: sumTag.as_on } };
  })();

  const handleJumpToDetailed = ({ ptype, btype }) => {
    setPivotFilter({ ptype, btype });
    setSubview("detailed");
    setFreqFilter("all");
  };
  const clearPivotFilter = () => setPivotFilter(null);

  return (
    <div className="space-y-4">
      {/* Subview toggle */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border overflow-hidden" data-testid="outstanding-view-toggle">
          <button
            onClick={() => { setSubview("pivot"); setPivotFilter(null); }}
            className={`px-4 py-1.5 text-xs font-semibold ${subview === "pivot" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600"}`}
            data-testid="outstanding-view-pivot"
          >Matrix View</button>
          <button
            onClick={() => setSubview("detailed")}
            className={`px-4 py-1.5 text-xs font-semibold ${subview === "detailed" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600"}`}
            data-testid="outstanding-view-detailed"
          >Detailed</button>
          <button
            onClick={() => { setSubview("report"); setPivotFilter(null); }}
            className={`px-4 py-1.5 text-xs font-semibold ${subview === "report" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600"}`}
            data-testid="outstanding-view-report"
          >Report View</button>
        </div>
        {pivotFilter && subview === "detailed" && (
          <div className="flex items-center gap-2 text-xs bg-blue-50 text-[#0066CC] px-3 py-1.5 rounded-full" data-testid="pivot-filter-chip">
            <span>Filter: <strong>{(REVENUE_STREAMS.find(s => s.key === pivotFilter.ptype)?.label) || pivotFilter.ptype}</strong> × <strong>{pivotFilter.btype}</strong></span>
            <button onClick={clearPivotFilter} className="text-[#0066CC] hover:text-red-600 font-bold" data-testid="pivot-filter-clear" title="Clear filter">×</button>
          </div>
        )}
      </div>

      {subview === "pivot" ? (
        <>
          <OutstandingPivot data={data} onJumpToDetailed={handleJumpToDetailed} />
        </>
      ) : subview === "report" ? (
        <OutstandingReportView />
      ) : (
      <>
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="outstanding-freq-tabs">
        <span className="text-[11px] text-slate-500 font-medium pr-1">Bill line:</span>
        {FREQ_TABS.map(t => (
          <button key={t.id} data-testid={`outstanding-freq-${t.id}`} onClick={() => setFreqFilter(t.id)}
            className={`px-3 py-1 text-[11px] rounded-full border transition ${freqFilter === t.id ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["Projects", summary.total_projects || 0, "text-slate-800"],
          ["Expected Billed", fmt(summary.expected_billed || 0), "text-[#0066CC]"],
          ["Collected", fmt(summary.total_collected || 0), "text-emerald-600"],
          ["Outstanding", fmt(summary.total_outstanding || 0), "text-red-600"],
        ].map(([l, v, c]) => (
          <div key={l} className="bg-white rounded-lg border p-3 text-center"><p className={`text-xl font-bold ${c}`}>{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
        ))}
      </div>
      {summary.as_on && (
        <p className="text-[11px] text-slate-500">
          Accrual computed as on <strong>{summary.as_on}</strong>. Recurring lines accrue per completed period.
          {freqFilter !== "all" && <span className="ml-2 text-[#0066CC]">Filter: <strong>{FREQ_TABS.find(t => t.id === freqFilter)?.label}</strong> billing lines only.</span>}
        </p>
      )}
      <ProjectOutstandingAccordion projects={projects} onSnapshot={onSnapshot} />
      </>
      )}
    </div>
  );
}

export default function ReportsPanel({ projects, onSnapshot }) {
  const isRestrictedCollectionsUser = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user_data") || "{}");
      if (u.role === "admin") return false;
      return !!u.can_access_collections;
    } catch { return false; }
  })();
  const [activeReport, setActiveReport] = useState("outstanding");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePreset, setActivePreset] = useState("current_fy");
  const [dateRange, setDateRange] = useState(() => {
    const r = getDatePreset("current_fy");
    return { start: r.start, end: r.end };
  });
  const [weeklyTargetWeeks, setWeeklyTargetWeeks] = useState(6);

  const applyPreset = (presetId) => {
    const r = getDatePreset(presetId);
    setActivePreset(presetId);
    setDateRange({ start: r.start, end: r.end });
  };

  const loadReport = useCallback(async (type) => {
    setLoading(true);
    try {
      let url = "";
      if (type === "revenue") url = `/collections/reports/revenue-by-stream?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      else if (type === "outstanding") url = `/collections/reports/project-outstanding`;
      else if (type === "weekly-targets") url = `/collections/reports/weekly-targets?weeks=${weeklyTargetWeeks}`;
      const res = await api.get(url);
      setReportData(res.data);
    } catch { toast.error("Failed to load report"); }
    setLoading(false);
  }, [dateRange, weeklyTargetWeeks]);

  useEffect(() => { loadReport(activeReport); }, [activeReport, loadReport]);

  const ALL_TABS = [
    { id: "weekly-targets", label: "Weekly Targets" },
    { id: "outstanding", label: "Project Outstanding" },
    { id: "revenue", label: "Revenue by Stream" },
  ];
  const TABS = isRestrictedCollectionsUser
    ? ALL_TABS.filter(t => ["weekly-targets", "outstanding"].includes(t.id))
    : ALL_TABS;

  useEffect(() => {
    if (isRestrictedCollectionsUser && !["weekly-targets", "outstanding"].includes(activeReport)) {
      setActiveReport("weekly-targets");
    }
  }, [isRestrictedCollectionsUser, activeReport]);

  const dateRangeApplies = ["revenue"].includes(activeReport);

  return (
    <div className="space-y-4" data-testid="reports-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Reports</h1>
        <div className="flex items-center gap-2">
          {dateRangeApplies && activeReport !== "revenue" && (
            <>
              <Input type="date" className="h-9 text-xs w-[130px]" value={dateRange.start} onChange={e => { setActivePreset(""); setDateRange(p => ({ ...p, start: e.target.value })); }} />
              <span className="text-xs text-slate-400">to</span>
              <Input type="date" className="h-9 text-xs w-[130px]" value={dateRange.end} onChange={e => { setActivePreset(""); setDateRange(p => ({ ...p, end: e.target.value })); }} />
            </>
          )}
          {activeReport === "revenue" && (
            <FYSelector value={activePreset} onChange={(presetId) => applyPreset(presetId)} />
          )}
          <Button data-testid="reports-refresh" variant="default" size="icon" onClick={() => loadReport(activeReport)} className="h-9 w-9 bg-[#0066CC] hover:bg-[#0052A3]" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {dateRangeApplies && activeReport !== "revenue" && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="reports-date-presets">
          <span className="text-[11px] text-slate-500 font-medium pr-1">Quick:</span>
          {PRESETS.map(p => (
            <button key={p.id} data-testid={`preset-${p.id}`} onClick={() => applyPreset(p.id)}
              className={`px-2.5 py-1 text-[11px] rounded-full border transition ${activePreset === p.id ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {p.label}
            </button>
          ))}
          {activePreset && <span className="text-[10px] text-slate-400 ml-1.5">{getDatePreset(activePreset).label}</span>}
        </div>
      )}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {TABS.map(t => (<button key={t.id} onClick={() => setActiveReport(t.id)} className={`px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${activeReport === t.id ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t.label}</button>))}
      </div>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : reportData && (
        <>
          {activeReport === "weekly-targets" && reportData.weeks && (
            <div className="space-y-4" data-testid="weekly-targets-report">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {reportData.last_week ? (
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Last Week</p>
                          <p className="text-sm font-medium text-slate-700">{reportData.last_week.label}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-[10px] font-bold ${reportData.last_week.achievement_pct >= 100 ? "bg-emerald-100 text-emerald-700" : reportData.last_week.achievement_pct >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {reportData.last_week.achievement_pct}% achieved
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div><p className="text-lg font-bold text-slate-800">{fmt(reportData.last_week.target)}</p><p className="text-[10px] text-slate-500">Target</p></div>
                        <div><p className="text-lg font-bold text-emerald-600">{fmt(reportData.last_week.collected)}</p><p className="text-[10px] text-slate-500">Collected · {reportData.last_week.receipts} receipt{reportData.last_week.receipts !== 1 ? "s" : ""}</p></div>
                        <div><p className={`text-lg font-bold ${reportData.last_week.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>{reportData.last_week.gap > 0 ? `${fmt(reportData.last_week.gap)}` : `+${fmt(reportData.last_week.surplus)}`}</p><p className="text-[10px] text-slate-500">{reportData.last_week.gap > 0 ? "Shortfall" : "Surplus"}</p></div>
                      </div>
                      <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${reportData.last_week.achievement_pct >= 100 ? "bg-emerald-500" : reportData.last_week.achievement_pct >= 70 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${Math.min(100, reportData.last_week.achievement_pct)}%` }} />
                      </div>
                    </CardContent>
                  </Card>
                ) : <Card className="border-slate-200"><CardContent className="p-4 text-center text-slate-400">No last-week data</CardContent></Card>}

                {reportData.current_week && (
                  <Card className="border-2 border-[#0066CC]/30 bg-[#F0F7FF]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-[#0066CC]">Current Week</p>
                          <p className="text-sm font-medium text-slate-800">{reportData.current_week.label} <span className="text-[10px] text-slate-500">({reportData.days_remaining_in_week} day{reportData.days_remaining_in_week !== 1 ? "s" : ""} left)</span></p>
                        </div>
                        <span className={`px-2 py-1 rounded text-[10px] font-bold ${reportData.current_week.achievement_pct >= 100 ? "bg-emerald-100 text-emerald-700" : reportData.current_week.achievement_pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {reportData.current_week.achievement_pct}% MTD
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div><p className="text-lg font-bold text-slate-800">{fmt(reportData.current_week.target)}</p><p className="text-[10px] text-slate-500">Base Target</p></div>
                        {reportData.current_week.carry_forward > 0 && (
                          <div><p className="text-lg font-bold text-orange-600">+{fmt(reportData.current_week.carry_forward)}</p><p className="text-[10px] text-slate-500">Carry-forward</p></div>
                        )}
                        <div><p className="text-lg font-bold text-[#0066CC]">{fmt(reportData.current_week.effective_target)}</p><p className="text-[10px] text-slate-500">Effective Target</p></div>
                        <div><p className="text-lg font-bold text-emerald-600">{fmt(reportData.current_week.collected)}</p><p className="text-[10px] text-slate-500">Collected so far</p></div>
                      </div>
                      <div className="mt-3 h-2 bg-white rounded-full overflow-hidden border border-slate-200">
                        <div className={`h-full rounded-full ${reportData.current_week.achievement_pct >= 100 ? "bg-emerald-500" : "bg-[#0066CC]"}`} style={{ width: `${Math.min(100, reportData.current_week.collected / Math.max(1, reportData.current_week.effective_target) * 100)}%` }} />
                      </div>
                      {reportData.current_week.gap > 0 && reportData.days_remaining_in_week > 0 && (
                        <p className="text-[11px] text-slate-600 mt-2">
                          Need <span className="font-bold text-red-600">{fmt(Math.max(0, reportData.current_week.effective_target - reportData.current_week.collected))}</span> in next {reportData.days_remaining_in_week} day{reportData.days_remaining_in_week !== 1 ? "s" : ""} (~{fmt(Math.max(0, reportData.current_week.effective_target - reportData.current_week.collected) / Math.max(1, reportData.days_remaining_in_week))}/day)
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Show last:</span>
                {[4, 6, 8, 12].map(n => (
                  <button key={n} onClick={() => setWeeklyTargetWeeks(n)} className={`px-2.5 py-1 text-xs rounded ${weeklyTargetWeeks === n ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{n} weeks</button>
                ))}
              </div>

              <Card><CardContent className="p-4 overflow-x-auto">
                <table className="w-full text-xs" data-testid="weekly-targets-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-semibold text-slate-600">Week</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Base Target</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Carry-forward</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Effective Target</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Collected</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Gap / Surplus</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Achievement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.weeks.map(w => (
                      <tr key={w.week_start} className={`border-b border-slate-50 ${w.is_current ? "bg-blue-50/40" : ""}`}>
                        <td className="py-2.5 px-2 font-medium text-slate-800">{w.label} {w.is_current && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#0066CC] text-white text-[9px] font-bold">NOW</span>}</td>
                        <td className="py-2.5 px-2 text-right text-slate-600">{fmt(w.target)}</td>
                        <td className="py-2.5 px-2 text-right text-orange-600 font-medium">{w.carry_forward > 0 ? `+${fmt(w.carry_forward)}` : "—"}</td>
                        <td className="py-2.5 px-2 text-right font-bold text-[#0066CC]">{fmt(w.effective_target)}</td>
                        <td className="py-2.5 px-2 text-right font-semibold text-emerald-700">{fmt(w.collected)}</td>
                        <td className={`py-2.5 px-2 text-right font-bold ${w.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>{w.gap > 0 ? `-${fmt(w.gap)}` : `+${fmt(w.surplus)}`}</td>
                        <td className="py-2.5 px-2 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${w.achievement_pct >= 100 ? "bg-emerald-500" : w.achievement_pct >= 70 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${Math.min(100, w.achievement_pct)}%` }} />
                            </div>
                            <span className="text-[10px] font-bold w-9 text-right">{w.achievement_pct}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>

              <Card className="border-slate-200 bg-slate-50/60">
                <CardContent className="p-3 text-xs text-slate-600 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="font-semibold text-slate-800">Auto-derived target:</span> Each week&apos;s base target = FY annual target ÷ 52.
                    <span className="font-semibold text-slate-800 ml-2">Carry-forward:</span> Any shortfall from a previous week is added to the next week&apos;s effective target until cleared. Surplus does not roll forward (it just shows as positive achievement that week).
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {activeReport === "outstanding" && reportData.projects && (
            <OutstandingReport data={reportData} onSnapshot={onSnapshot} />
          )}
          {activeReport === "revenue" && reportData.streams && (
            <RevenueByStreamView reportData={reportData} />
          )}
        </>
      )}
    </div>
  );
}


/* ================================================================== */
/*  FYSelector — quick "Current FY / Last FY" toggle + FY dropdown     */
/* ================================================================== */
function FYSelector({ value, onChange }) {
  // Reactively derives options from today's date; value is a preset id.
  const now = new Date();
  const y = now.getFullYear();
  const isPostApr = now.getMonth() >= 3;
  const currentFyStart = isPostApr ? y : y - 1;
  const options = [
    { id: "current_fy", label: `Current FY (FY ${String(currentFyStart).slice(-2)}-${String(currentFyStart + 1).slice(-2)})` },
    { id: "last_fy",    label: `Last FY (FY ${String(currentFyStart - 1).slice(-2)}-${String(currentFyStart).slice(-2)})` },
  ];
  const currentValue = value === "last_fy" ? "last_fy" : "current_fy";
  return (
    <select
      className="h-9 border border-slate-200 rounded-md px-2 text-xs bg-white min-w-[220px]"
      value={currentValue}
      onChange={(e) => onChange(e.target.value)}
      data-testid="revenue-fy-selector"
    >
      {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
}

/* ================================================================== */
/*  RevenueByStreamView — dashlets + quarter / month cross-comparison   */
/* ================================================================== */
function RevenueByStreamView({ reportData }) {
  const streams = reportData.streams || {};
  // Only visible stream keys (bucketed set)
  const visibleStreams = REVENUE_STREAMS.filter(s =>
    !s.legacy || (streams[s.key] && streams[s.key].total_collected > 0)
  );

  // Derive month keys sorted asc from all streams
  const monthKeys = useMemo(() => {
    const set = new Set();
    for (const key of Object.keys(streams)) {
      const months = streams[key]?.months || {};
      Object.keys(months).forEach(m => set.add(m));
    }
    return [...set].sort();
  }, [streams]);

  // Aggregate month → stream_key → collected
  const monthlyMatrix = useMemo(() => {
    const grid = {};
    for (const mo of monthKeys) grid[mo] = {};
    for (const s of visibleStreams) {
      const months = streams[s.key]?.months || {};
      for (const mo of monthKeys) grid[mo][s.key] = (months[mo]?.collected) || 0;
    }
    return grid;
  }, [monthKeys, visibleStreams, streams]);

  // Group months into quarters (Apr-Jun / Jul-Sep / Oct-Dec / Jan-Mar for Indian FY)
  const quarterMatrix = useMemo(() => {
    const grid = {}; // qLabel → stream_key → collected
    for (const mo of monthKeys) {
      const [yr, mm] = mo.split("-").map(Number);
      // Indian FY: Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar
      let q, fyYearStart;
      if (mm >= 4 && mm <= 6) { q = "Q1"; fyYearStart = yr; }
      else if (mm >= 7 && mm <= 9) { q = "Q2"; fyYearStart = yr; }
      else if (mm >= 10 && mm <= 12) { q = "Q3"; fyYearStart = yr; }
      else { q = "Q4"; fyYearStart = yr - 1; }
      const label = `${q} FY${String(fyYearStart).slice(-2)}-${String(fyYearStart + 1).slice(-2)}`;
      if (!grid[label]) { grid[label] = { _sortKey: fyYearStart * 10 + (q === "Q1" ? 1 : q === "Q2" ? 2 : q === "Q3" ? 3 : 4) }; }
      for (const s of visibleStreams) {
        grid[label][s.key] = (grid[label][s.key] || 0) + (monthlyMatrix[mo][s.key] || 0);
      }
    }
    return grid;
  }, [monthKeys, monthlyMatrix, visibleStreams]);
  const quarterLabels = useMemo(() => Object.entries(quarterMatrix).sort((a, b) => a[1]._sortKey - b[1]._sortKey).map(([k]) => k), [quarterMatrix]);

  const monthNameShort = (m) => new Date(m + "-01T00:00:00").toLocaleDateString("en-IN", { month: "short", year: "2-digit" });

  return (
    <div className="space-y-5" data-testid="revenue-by-stream-view">
      {/* Dashlets — one per stream */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="revenue-stream-dashlets">
        {visibleStreams.map(s => {
          const d = streams[s.key];
          return (
            <Card key={s.key} className={`${!d ? "opacity-50" : ""}`} data-testid={`revenue-stream-card-${s.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="text-xs font-semibold text-slate-700">{s.label}</span>
                </div>
                <p className="text-xl font-bold" style={{ color: d ? s.color : "#ccc" }}>
                  {d ? fmt(d.total_collected) : "No data"}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quarter-wise Comparison */}
      <Card data-testid="revenue-quarterly-comparison">
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-800">Quarter-wise Stream Comparison</h3>
            <p className="text-[10px] text-slate-500">Collected revenue by stream, per Indian FY quarter</p>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Quarter</th>
                {visibleStreams.map(s => (
                  <th key={s.key} className="text-right px-3 py-2 font-semibold" style={{ color: s.color }}>{s.label}</th>
                ))}
                <th className="text-right px-3 py-2 font-semibold text-slate-800">Total</th>
              </tr>
            </thead>
            <tbody>
              {quarterLabels.length === 0 && (
                <tr><td colSpan={visibleStreams.length + 2} className="text-center text-slate-400 py-8">No revenue in the selected range.</td></tr>
              )}
              {quarterLabels.map(q => {
                const row = quarterMatrix[q];
                const total = visibleStreams.reduce((t, s) => t + (row[s.key] || 0), 0);
                return (
                  <tr key={q} className="border-b border-slate-50 hover:bg-blue-50/40">
                    <td className="px-3 py-2 font-medium text-slate-700">{q}</td>
                    {visibleStreams.map(s => (
                      <td key={s.key} className="px-3 py-2 text-right font-mono text-slate-600">
                        {(row[s.key] || 0) > 0 ? fmt(row[s.key]) : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-800">{fmt(total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Month-wise Comparison */}
      <Card data-testid="revenue-monthly-comparison">
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-800">Month-wise Stream Comparison</h3>
            <p className="text-[10px] text-slate-500">Collected revenue by stream, per calendar month</p>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Month</th>
                {visibleStreams.map(s => (
                  <th key={s.key} className="text-right px-3 py-2 font-semibold" style={{ color: s.color }}>{s.label}</th>
                ))}
                <th className="text-right px-3 py-2 font-semibold text-slate-800">Total</th>
              </tr>
            </thead>
            <tbody>
              {monthKeys.length === 0 && (
                <tr><td colSpan={visibleStreams.length + 2} className="text-center text-slate-400 py-8">No revenue in the selected range.</td></tr>
              )}
              {monthKeys.map(mo => {
                const row = monthlyMatrix[mo];
                const total = visibleStreams.reduce((t, s) => t + (row[s.key] || 0), 0);
                return (
                  <tr key={mo} className="border-b border-slate-50 hover:bg-blue-50/40">
                    <td className="px-3 py-2 font-medium text-slate-700">{monthNameShort(mo)}</td>
                    {visibleStreams.map(s => (
                      <td key={s.key} className="px-3 py-2 text-right font-mono text-slate-600">
                        {(row[s.key] || 0) > 0 ? fmt(row[s.key]) : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-800">{fmt(total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
